/**
 * Wix BI Events Type Definitions
 *
 * Types for Wix Business Intelligence event tracking.
 * Events are sent to Wix Partner Dashboard via the BI Events API.
 *
 * API Reference: https://dev.wix.com/docs/rest/api-reference/app-management/apps/bi-event/send-bi-event
 */

/**
 * Wix built-in event names (from Wix API enum)
 */
export type WixBuiltInEventName =
  | 'APP_DASHBOARD_LOADED'
  | 'PRIMARY_ACTION_PERFORMED'
  | 'APP_UPGRADED'
  | 'CUSTOM';

/**
 * Single BI Event payload for Wix REST API (POST /apps/v1/bi-event)
 *
 * Each event is sent as a separate request — no batch wrapper.
 */
export interface WixBiEventPayload {
  /** Wix built-in event type */
  eventName: WixBuiltInEventName;
  /** Custom event name (required when eventName is "CUSTOM") */
  customEventName?: string;
  /** Key-value event data — all values must be strings */
  eventData?: Record<string, string>;
}

/**
 * Event parameter types for type safety
 */
export interface BlogGenerationInitiatedParams {
  topic: string;
  word_count: number;
  language: string;
  has_images: boolean;
  has_infographics: boolean;
}

export interface BlogGenerationCompletedParams {
  topic: string;
  duration_ms: number;
  success: boolean;
  has_images?: boolean;
}

export interface BlogGenerationFailedParams {
  topic: string;
  error_type: string;
  error_message: string;
}

export interface ContentPublishedParams {
  platform: string;
  content_type: string;
  has_images: boolean;
}

export interface TrialCreditUsedParams {
  credits_remaining: number;
}

export interface PlanPurchasedParams {
  plan_id: string;
  credits_allocated: number;
}

export interface FeatureAccessedParams {
  feature_name: string;
  action: string;
}

export interface ContentDownloadedParams {
  format: 'html' | 'markdown' | 'word';
}

export interface SettingsChangedParams {
  setting_name: string;
  old_value: string | number | boolean;
  new_value: string | number | boolean;
}

export interface BrandProfileSelectedParams {
  profile_id: string;
  profile_name: string;
}

export interface LanguageChangedParams {
  language: string;
}

/**
 * Configuration for Wix BI Events Service
 */
export interface WixBiEventsConfig {
  /** Wix App ID */
  appId: string;
  /** Flush interval in milliseconds (default: 10000) */
  flushIntervalMs?: number;
  /** Maximum events queued before force-flush (default: 50) */
  maxQueueSize?: number;
  /** Enable/disable tracking (default: true) */
  enabled?: boolean;
  /** Enable debug logging (default: false) */
  debug?: boolean;
}

/**
 * Mapping from app-level event names to Wix API event format.
 *
 * Built-in events use a Wix enum value directly.
 * Custom events use eventName: "CUSTOM" + customEventName.
 */
export const EVENT_NAME_MAP: Record<string, { eventName: WixBuiltInEventName; customEventName?: string }> = {
  // Built-in Wix events
  app_loaded: { eventName: 'APP_DASHBOARD_LOADED' },
  blog_generation_initiated: { eventName: 'PRIMARY_ACTION_PERFORMED' },
  plan_selected: { eventName: 'APP_UPGRADED' },

  // Custom events
  blog_generation_completed: { eventName: 'CUSTOM', customEventName: 'blog_generation_completed' },
  blog_generation_failed: { eventName: 'CUSTOM', customEventName: 'blog_generation_failed' },
  trial_credit_used: { eventName: 'CUSTOM', customEventName: 'trial_credit_used' },
  credit_shortage: { eventName: 'CUSTOM', customEventName: 'credit_shortage' },
  upgrade_prompted: { eventName: 'CUSTOM', customEventName: 'upgrade_prompted' },
  content_published: { eventName: 'CUSTOM', customEventName: 'content_published' },
  content_downloaded: { eventName: 'CUSTOM', customEventName: 'content_downloaded' },
  plan_purchased: { eventName: 'CUSTOM', customEventName: 'plan_purchased' },
  plan_upgraded: { eventName: 'CUSTOM', customEventName: 'plan_upgraded' },
  plan_cancelled: { eventName: 'CUSTOM', customEventName: 'plan_cancelled' },
  feature_accessed: { eventName: 'CUSTOM', customEventName: 'feature_accessed' },
  settings_changed: { eventName: 'CUSTOM', customEventName: 'settings_changed' },
  brand_profile_selected: { eventName: 'CUSTOM', customEventName: 'brand_profile_selected' },
  language_changed: { eventName: 'CUSTOM', customEventName: 'language_changed' },
  image_settings_configured: { eventName: 'CUSTOM', customEventName: 'image_settings_configured' },
};

/**
 * Event name constants for consistency (used in trackBiEvent calls)
 */
export const WixBiEventName = {
  // App lifecycle
  APP_LOADED: 'app_loaded',

  // Blog Generation
  BLOG_GENERATION_INITIATED: 'blog_generation_initiated',
  BLOG_GENERATION_COMPLETED: 'blog_generation_completed',
  BLOG_GENERATION_FAILED: 'blog_generation_failed',

  // Publishing
  CONTENT_PUBLISHED: 'content_published',
  CONTENT_DOWNLOADED: 'content_downloaded',

  // Credits & Subscriptions
  TRIAL_CREDIT_USED: 'trial_credit_used',
  PLAN_PURCHASED: 'plan_purchased',
  PLAN_UPGRADED: 'plan_upgraded',
  PLAN_CANCELLED: 'plan_cancelled',
  PLAN_SELECTED: 'plan_selected',

  // Feature Usage
  FEATURE_ACCESSED: 'feature_accessed',
  SETTINGS_CHANGED: 'settings_changed',
  BRAND_PROFILE_SELECTED: 'brand_profile_selected',
  LANGUAGE_CHANGED: 'language_changed',

  // Additional
  IMAGE_SETTINGS_CONFIGURED: 'image_settings_configured',
  CREDIT_SHORTAGE: 'credit_shortage',
  UPGRADE_PROMPTED: 'upgrade_prompted',
} as const;

export type WixBiEventNameType = typeof WixBiEventName[keyof typeof WixBiEventName];
